import { useEffect, useRef, useState, type PointerEvent, type ReactNode } from 'react'
import {
  paintScratchCover,
  scratchCoverage,
  SCRATCH_HEIGHT,
  SCRATCH_RADIUS,
  SCRATCH_WIDTH,
  type ScratchPoint,
} from './scratch-surface'
import styles from './ScratchCard.module.css'

type Props = {
  children: ReactNode
  onReveal: () => void
  initiallyRevealed?: boolean
  revealThreshold?: number
}

/** Visual interaction only: the caller owns the persisted prize and all reward rules. */
export function ScratchCard({
  children,
  onReveal,
  initiallyRevealed = false,
  revealThreshold = 0.52,
}: Props) {
  const canvas = useRef<HTMLCanvasElement>(null)
  const points = useRef<ScratchPoint[]>([])
  const previous = useRef<ScratchPoint | null>(null)
  const pointer = useRef<number | null>(null)
  const covered = useRef(new Set<number>())
  const frame = useRef<number | null>(null)
  const complete = useRef(initiallyRevealed)
  const [revealed, setRevealed] = useState(initiallyRevealed)
  const [progress, setProgress] = useState(initiallyRevealed ? 100 : 0)

  useEffect(() => {
    const element = canvas.current
    if (!element || complete.current) return
    const ratio = Math.min(window.devicePixelRatio || 1, 2)
    element.width = SCRATCH_WIDTH * ratio
    element.height = SCRATCH_HEIGHT * ratio
    const context = element.getContext('2d')
    if (context) {
      context.scale(ratio, ratio)
      paintScratchCover(context)
    }
    return () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current)
    }
  }, [])

  function reveal() {
    if (complete.current) return
    complete.current = true
    setRevealed(true)
    setProgress(100)
    onReveal()
  }

  function flush() {
    frame.current = null
    const context = canvas.current?.getContext('2d')
    if (!context || complete.current) return
    context.globalCompositeOperation = 'destination-out'
    context.strokeStyle = '#000'
    context.fillStyle = '#000'
    context.lineWidth = SCRATCH_RADIUS * 2
    context.lineCap = 'round'
    context.lineJoin = 'round'
    let coverage = 0
    for (const point of points.current.splice(0)) {
      const start = previous.current ?? point
      context.beginPath()
      context.moveTo(start.x, start.y)
      context.lineTo(point.x, point.y)
      context.stroke()
      context.beginPath()
      context.arc(point.x, point.y, SCRATCH_RADIUS, 0, Math.PI * 2)
      context.fill()
      coverage = scratchCoverage(covered.current, start, point)
      previous.current = point
    }
    setProgress(
      Math.min(
        100,
        Math.round((coverage / Math.max(0.1, Math.min(1, revealThreshold))) * 100)
      )
    )
    if (coverage >= Math.max(0.1, Math.min(1, revealThreshold))) reveal()
  }

  function addPoint(event: PointerEvent<HTMLCanvasElement>) {
    if (complete.current) return
    const rect = event.currentTarget.getBoundingClientRect()
    if (!rect.width || !rect.height) return
    points.current.push({
      x: ((event.clientX - rect.left) * SCRATCH_WIDTH) / rect.width,
      y: ((event.clientY - rect.top) * SCRATCH_HEIGHT) / rect.height,
    })
    if (frame.current === null) frame.current = requestAnimationFrame(flush)
  }

  function endStroke(event: PointerEvent<HTMLCanvasElement>) {
    if (pointer.current !== event.pointerId) return
    if (frame.current !== null) cancelAnimationFrame(frame.current)
    if (points.current.length) flush()
    previous.current = null
    pointer.current = null
    if (event.currentTarget.hasPointerCapture?.(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId)
  }

  return (
    <div className={styles.root}>
      <div className={`${styles.ticket} ${revealed ? styles.revealed : ''}`}>
        <div className={styles.result} aria-hidden={!revealed} inert={!revealed}>
          {children}
        </div>
        <canvas
          ref={canvas}
          className={styles.cover}
          aria-hidden="true"
          onPointerDown={(event) => {
            if (
              pointer.current !== null ||
              (event.pointerType === 'mouse' && event.button !== 0)
            )
              return
            pointer.current = event.pointerId
            previous.current = null
            event.currentTarget.setPointerCapture(event.pointerId)
            addPoint(event)
          }}
          onPointerMove={(event) => {
            if (pointer.current === event.pointerId) addPoint(event)
          }}
          onPointerUp={endStroke}
          onPointerCancel={endStroke}
          onLostPointerCapture={endStroke}
        />
        {!revealed && progress === 0 && <span className={styles.sheen} aria-hidden />}
      </div>
      {!revealed && (
        <>
          <div
            className={styles.progress}
            role="progressbar"
            aria-label="Progresso da raspadinha"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progress}
          >
            <span style={{ width: `${progress}%` }} />
          </div>
          <p className={styles.hint}>
            {progress > 0
              ? 'Continue raspando… sua surpresa está quase aparecendo!'
              : 'Passe o dedo ou o mouse sobre a área prateada.'}
          </p>
          <button type="button" className={styles.revealButton} onClick={reveal}>
            Revelar sem raspar
          </button>
        </>
      )}
    </div>
  )
}
